// Items and their effects: use, hits, created items.
import type { ItemKey } from './items.js';
import type { CustomItemDef } from './modes.js';
import type { Rider, RaceEvent } from './race-types.js';
import type { Race } from './race.js';
import { mod } from './track.js';

/**
 * A crash. `s`: seconds from the fall until the rider rides again, `remount`: the last of them, up and getting back on
 * the bike, when nobody can ride into them any more. `grace`: seconds after that when the rider cannot fall again.
 * `contact`: metres behind a fallen rider where a wheel hits them, `width`: road units either side (about 95 cm).
 * `brake`: how fast the slide along the road dies (1/s), `stop`: speed (m/s) under which the rider lies still.
 */
export const CRASH = { s: 5, remount: 1.6, grace: 2, contact: 1.2, width: 0.4, brake: 3, stop: 0.4 };

/** Brings a rider down. A shield takes the fall and lets them ride through. `via`: the fallen rider they rode into. */
export function crash(race: Race, target: Rider, src: Rider | null, via: Rider | null = null): boolean {
  if (target.out || target.finishT != null || target.invuln > 0 || target.fx.crash || race.t < target.startT) return false;
  const ok = race.afflict(target, src, r => { race.addFx(r, 'crash', CRASH.s); r.invuln = CRASH.s + CRASH.grace; }, 'crash');
  if (!ok) { target.invuln = CRASH.grace; return false; }
  // only the rider aimed at scores: a pile-up must not decide a game counted in hits
  if (src && src !== target) src.hits++;
  race.emit({ type: 'hit', kind: 'crash', who: target.id, src: src && src !== target ? src.id : null, via: via ? via.id : null });
  return true;
}

/** Riders on the ground, where a wheel can still hit them. */
export const fallen = (race: Race): Rider[] => race.riders.filter(r => !r.out && (r.fx.crash ?? 0) > CRASH.remount);

/** Every rider who reached a fallen rider on the same line this step goes down too: that is what makes a pile-up. */
export function pileUp(race: Race) {
  const down = fallen(race), L = race.track.L;
  if (!down.length) return;
  for (const r of race.riders) {
    if (r.out || r.fx.crash || r.invuln > 0 || r.finishT != null) continue;
    for (const c of down) {
      if (Math.abs(c.lane - r.lane) >= CRASH.width) continue;
      // along the loop: a lapped rider hits the same pile. Only from behind: a rider already level or past rides away.
      const gap = mod(c.s - r.s + L / 2, L) - L / 2;
      if (gap <= CRASH.contact && gap + (r.s - r.prevS) >= 0) { crash(race, r, null, c); break; }
    }
  }
}

export function afflict(race: Race, target: Rider | null, src: Rider | null, fn: (r: Rider) => void, what: string): boolean {
  if (!target || target.out) return false;
  if (target.shield > 0) { target.shield = 0; race.emit({ type: 'blocked', who: target.id, src: src ? src.id : null, what }); return false; }
  fn(target);
  return true;
}

/** Effect of a created item on a rider. */
export function applyCustom(race: Race, t: Rider, d: CustomItemDef) {
  const e = d.content.effect;
  if (e.speed != null) t.v *= e.speed;
  if (e.shield) t.shield = Math.max(t.shield, e.shield);
  if (e.spin) race.addFx(t, 'spin', Math.min(1.5, d.content.duration));
  if (e.jam) race.addFx(t, 'jam', d.content.duration);
  race.addFx(t, `c:${d.id}`, d.content.duration);
}

/** Hit by a trap or a missile: banana, official missile, or created item (`kind` = the item's key). */
export function hit(race: Race, target: Rider, kind: 'banana' | 'shell' | ItemKey, src: Rider | null | undefined) {
  if (target.invuln > 0 || target.finishT != null) return;
  const d = race.custom.get(kind);
  const effect = (r: Rider) => {
    r.invuln = 1.2;
    if (d) race.applyCustom(r, d);
    else if (kind === 'banana') { r.v *= 0.6; race.addFx(r, 'heavy', 3); race.addFx(r, 'spin', 0.8); }
    else { r.v *= 0.45; race.addFx(r, 'spin', 1.5); }
  };
  const ok = d && d.content.blockable === false ? (effect(target), true) : race.afflict(target, src ?? null, effect, kind);
  if (!ok) return;
  if (src && src !== target) src.hits++;
  race.emit({ type: 'hit', kind, who: target.id, src: src && src !== target ? src.id : null });
}

export function useItem(race: Race, r: Rider): boolean {
  const it = r.item;
  if (!it || r.out || r.fx.crash || race.state !== 'running') return false;
  r.item = null; r.itemsUsed++;
  const ev: RaceEvent & { targets?: number[] } = { type: 'use', who: r.id, item: it };
  const d = race.custom.get(it);
  if (d) { race.useCustom(r, d, ev); race.emit(ev); return true; }
  switch (it) {
    case 'turbo': race.addFx(r, 'turbo', 4); break;
    case 'banana': race.hazards.push({ s: r.s - 2.5, lane: r.lane, owner: r, age: 0 }); break;
    case 'shell': {
      const tgt = race.nearestAhead(r);
      race.shells.push({ s: r.s + 1.5, lane: r.lane, target: tgt, owner: r, life: 12, dist: 0 });
      ev.target = tgt ? tgt.id : null;
      break;
    }
    case 'shield': r.shield = 10; break;
    case 'magnet': race.addFx(r, 'magnet', 8); break;
    case 'jam': {
      const tgt = race.nearestAhead(r, 250) || race.leaderExcept(r);
      ev.target = tgt ? tgt.id : null;
      ev.ok = race.afflict(tgt, r, t => race.addFx(t, 'jam', 6), 'jam');
      break;
    }
    case 'storm': {
      const tgt = race.leaderExcept(r);
      ev.target = tgt ? tgt.id : null;
      ev.ok = race.afflict(tgt, r, t => race.addFx(t, 'headwind', 5), 'storm');
      break;
    }
    case 'fog': {
      ev.targets = [];
      for (const o of race.riders) if (o !== r && !o.out && o.s > r.s && o.finishT == null && race.afflict(o, r, t => race.addFx(t, 'fog', 4), 'fog')) ev.targets.push(o.id);
      break;
    }
    case 'bomb': race.addFx(r, 'charge', 5); r.chargeSum = 0; r.chargeT = 0; break;
    case 'crash': {
      // a rider who can actually fall: not one already down, protected, or still waiting for their gun
      const can = (o: Rider) => !o.fx.crash && o.invuln <= 0 && race.t >= o.startT;
      const tgt = race.nearestAhead(r, 250, can) || race.riders.filter(o => o !== r && !o.out && o.finishT == null && can(o)).sort((a, b) => b.s - a.s)[0] || null;
      ev.target = tgt ? tgt.id : null;
      ev.ok = !!tgt && crash(race, tgt, r);
      break;
    }
  }
  race.emit(ev);
  return true;
}

/** Triggers a created item: immediate effect on its targets, trap dropped behind, or missile. */
export function useCustom(race: Race, r: Rider, d: CustomItemDef, ev: RaceEvent & { targets?: number[] }) {
  const C = d.content, key: ItemKey = `c:${d.id}`;
  if (C.delivery === 'trap') { race.hazards.push({ s: r.s - 2.5, lane: r.lane, owner: r, age: 0, item: key }); return; }
  if (C.delivery === 'missile') {
    const tgt = C.target === 'leader' ? race.leaderExcept(r) : race.nearestAhead(r);
    race.shells.push({ s: r.s + 1.5, lane: r.lane, target: tgt, owner: r, life: 12, dist: 0, item: key });
    ev.target = tgt ? tgt.id : null;
    return;
  }
  const live = (o: Rider) => o !== r && !o.out && o.finishT == null;
  const targets = C.target === 'self' ? [r]
    : C.target === 'ahead' ? [race.nearestAhead(r, 250) || race.leaderExcept(r)].filter((o): o is Rider => !!o)
      : C.target === 'leader' ? [race.leaderExcept(r)].filter((o): o is Rider => !!o)
        : C.target === 'allAhead' ? race.riders.filter(o => live(o) && o.s > r.s) : race.riders.filter(live);
  ev.targets = targets.filter(t => (t === r || C.blockable === false ? (race.applyCustom(t, d), true) : race.afflict(t, r, x => race.applyCustom(x, d), key))).map(t => t.id);
}
